import { Alert, Box, Grid, Typography } from '@mui/material'
import { TextFieldWithLabel } from 'Components/TextField'
import { type lookup, type location } from 'core/types'
import { MapModule, useBingMaps } from 'hooks/use-bingMaps'
import { useEffect, useState } from 'react'
import { CheckBoxWithLabel } from 'Components/CheckBox'
import { DatePickerWithLabel } from 'Components/DatePicker'
import { SelectWithLabel } from 'Components/SelectWithLabel'
import { sendGet } from 'hooks/use-fetch'
import { toast } from 'react-toastify'
import { SchoolIcon } from 'Assets/SVGs/school'
import { TextWithLabel } from 'Components/TextWithLabel'

interface LocationDetailsProps {
  onChange: (e: any) => void
  onPlaceSelected: (place: Microsoft.Maps.ISuggestionResult) => Promise<void>
  onDeleteChecked?: (checked: boolean) => void
  onISDChanging?: (confirmed: boolean) => void
  editData: location
  childID: number | null
  onBlur?: (dat: location) => void
  hidePrimary?: boolean
  showHomelessOption: boolean
  // hides street and apartment without unmounting them, so the address autosuggest stays attached
  hideStreet?: boolean
  errors?: string[]
  hideToastErrors?: boolean
}

interface DistrictISD {
  districtID: number
  districtName: string
  isdName: string
  isOnboardedISD: boolean
  isdIntakeProviderName: string | null
}

export function LocationDetails (props: LocationDetailsProps): JSX.Element {
  const [isError] = useState(false)
  const [homeless, setHomeless] = useState<boolean>(props.editData.isHomeless ?? false)
  const [residentOptions, setResidentOptions] = useState<lookup[] | null>(null)
  const [districtISD, setDistrictISD] = useState<DistrictISD>({ districtID: props.editData.districtID ?? 0, districtName: props.editData.districtName ?? '', isdName: props.editData.isdName ?? '', isOnboardedISD: true, isdIntakeProviderName: null })
  const [isdChanged, setISDChanged] = useState(false)
  const [originalISD, setOriginalISD] = useState<string>('')
  const [othersAffected, setOthersAffected] = useState<string[]>([])
  const [selectedLocation, setSelectedLocation] = useState<Microsoft.Maps.ISuggestionResult | null>(null)
  const [deleteChecked, setDeleteChecked] = useState(false)
  const [confirmTransfer, setConfirmTransfer] = useState(false)

  useEffect(() => {
    setOriginalISD(props.editData.isdName ?? '')
  }, [])

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      if (props.childID !== null) {
        const { response: rsp1, error: err1 } = await sendGet(`/CareNetworkEdit/MembersAtLocation?locationID=${props.editData.id}&excludingChildID=${props.childID}`, {})
        if (err1[0] === '' && rsp1 !== null) {
          setOthersAffected(rsp1 as string[])
        }
      }
    }
    void loadData()
  }, [props.childID])

  const handleBlur = (): void => {
    try {
      validateLocation(props.editData)
    } catch { }
  }

  const unaccompaniedOptions: Array<{ id: boolean, name: string }> = [
    { id: true, name: 'Yes' },
    { id: false, name: 'No' }
  ]

  const placeSelected = async (result: Microsoft.Maps.ISuggestionResult): Promise<void> => {
    setSelectedLocation(result)
    if (props.onPlaceSelected !== undefined) {
      await props.onPlaceSelected(result)
    }
  }

  const locationValidated = (isValid: boolean): void => {
    if (props.onBlur !== undefined) {
      props.onBlur(props.editData)
    }
  }

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      if (props.childID !== null && selectedLocation !== null) {
        const isPrimary = (props.editData.isPrimary ?? false)
        const { response: rsp2, error: err2 } = await sendGet(`/CareNetworkEdit/DistrictAtLocation?latitude=${selectedLocation.location.latitude}&longitude=${selectedLocation.location.longitude}`, {})
        if (err2[0] === '' && rsp2 !== null) {
          const districtData = rsp2 as DistrictISD
          setDistrictISD(districtData)
          setISDChanged(isPrimary && districtData.isdName !== originalISD && originalISD !== '')
          if (props.onISDChanging !== undefined && isPrimary && districtData.isdName !== originalISD && districtData.isOnboardedISD && !confirmTransfer) {
            props.onISDChanging(false)
          }
        }
      }
    }
    void loadData()
  }, [props.childID, props.hidePrimary, props.editData.isPrimary, selectedLocation])

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  const { validateLocation } = useBingMaps({ modules: [MapModule.AutoSuggest], selectFunction: placeSelected, inputID: 'address', onResultVerified: locationValidated })

  useEffect(() => {
    if (homeless && residentOptions === null) {
      const loadData = async (): Promise<void> => {
        const { response: rsp, error } = await sendGet('/CareNetworkEdit/HomelessResidenceOptions', {})
        if (error[0] === '' && rsp !== null) {
          setResidentOptions(rsp)
        }
      }
      void loadData()
    }
  }, [homeless])

  const valueChanged = (field: string, newValue: any): void => {
    props.onChange({ target: { name: field, value: newValue } })
  }

  const handleHomelessChanged = (newValue: boolean): void => {
    setHomeless(newValue)
    valueChanged('isHomeless', newValue)
  }

  const deleteAddressChanged = (newValue: boolean): void => {
    setDeleteChecked(newValue)
    props.onDeleteChecked?.(newValue)
  }

  const transferConfirmed = (newValue: boolean): void => {
    setConfirmTransfer(newValue)
    props.onISDChanging?.(newValue)
  }

  useEffect(() => {
    if (props.hideToastErrors !== undefined && !props.hideToastErrors) {
      props.errors?.forEach((err) => {
        toast.error(err)
      })
    }
  }, [props.errors])

  return (<>
    {isError && (
      <Typography>This location could not be found.</Typography>
    )}
    <Box sx={{ display: (props.hideStreet ?? false) ? 'none' : 'contents' }} data-testid='streetFields'>
      <TextFieldWithLabel
        ariaRole='combobox'
        ariaExpanded={false}
        ariaControls='as_containerSearch_address'
        label='Street'
        name='address'
        data-testid="address"
        required={!homeless}
        error={props.errors?.find(e => e === 'Address') !== undefined}
        showRequiredText={props.errors?.find(e => e === 'Address') !== undefined}
        autoComplete={'off'}
        onBlur={handleBlur}
        onChange={props.onChange}
        value={props.editData.address ?? ''}
      />
      <div id='searchBoxContainer'></div>
      <TextFieldWithLabel
        label='Apt'
        name='apartment'
        onBlur={handleBlur}
        onChange={props.onChange}
        value={props.editData.apartment ?? ''}
        dataTestId='apartment'
      />
    </Box>
    <Grid container columns={12} spacing={2}>
      <Grid item xs={6} sm={6}>
        <TextFieldWithLabel
          label='City'
          name='city'
          data-testid="city"
          required={true}
          error={props.errors?.find(e => e === 'City') !== undefined}
          showRequiredText={props.errors?.find(e => e === 'City') !== undefined}
          onBlur={handleBlur}
          onChange={props.onChange}
          value={props.editData.city ?? ''}
        />
      </Grid>
      <Grid item xs={3} sm={3}>
        <TextFieldWithLabel
          label='State'
          name='state'
          data-testid="state"
          required={true}
          error={props.errors?.find(e => e === 'State') !== undefined}
          showRequiredText={props.errors?.find(e => e === 'State') !== undefined}
          onBlur={handleBlur}
          onChange={props.onChange}
          maxLength={2}
          value={props.editData.state ?? ''}
        />
      </Grid>
      <Grid item xs={3} sm={3}>
        <TextFieldWithLabel
          label='Zip'
          name='zipCode'
          data-testid="zip"
          required={true}
          error={props.errors?.find(e => e === 'ZipCode') !== undefined}
          showRequiredText={props.errors?.find(e => e === 'ZipCode') !== undefined}
          onBlur={handleBlur}
          onChange={props.onChange}
          maxLength={5}
          value={props.editData.zipCode ?? ''}
        />
      </Grid>
    </Grid>
    {props.childID !== null && <Grid container sx={{ flexDirection: 'row' }} columnSpacing={2} marginLeft='10px' width='500px'>
      <Grid item marginTop='5px'>
        <SchoolIcon size={24} />
      </Grid>
      <Grid item>
        <Box>
          {districtISD.districtName}
        </Box>
        <Box>
          {districtISD.isdName}
        </Box>
      </Grid>
    </Grid>
    }
    <Grid container sx={{ flexDirection: 'row' }} columnSpacing={2}>
      {!(props.hidePrimary ?? false) && (
        <Grid item>
          <CheckBoxWithLabel
            edge='start'
            label='Is Primary Address'
            dataTestId="primary"
            name='isPrimary'
            setValue={(value) => { valueChanged('isPrimary', value) }}
            value={props.editData.isPrimary ?? false}
          />
        </Grid>
      )}
      {(props.editData.id !== 0) && props.childID !== null && !(props.editData.isPrimary ?? false) && // not letting them delete the primary address
        <Grid item>
          <CheckBoxWithLabel
            edge='start'
            label='Delete this Address'
            dataTestId="deleteChecked"
            name='deleteChecked'
            setValue={deleteAddressChanged}
            value={deleteChecked}
          />
        </Grid>
      }
    </Grid>
    {props.showHomelessOption && (
      <>
        <Box>
          <CheckBoxWithLabel
            edge='start'
            label='Is Currently Experiencing Homelessness'
            dataTestId="homeless"
            name='isHomeless'
            setValue={handleHomelessChanged}
            value={homeless}
          />
        </Box>

        {homeless && (
          <>
            <Grid container columns={12} spacing={1}>
              <Grid item xs={6} sm={6}>
                <DatePickerWithLabel
                  label='Homeless Start Date'
                  name='startDate'
                  data-testid="startDate"
                  required={true}
                  onChange={(value) => { valueChanged('startDate', value) }}
                  value={props.editData.startDate ?? null}
                />
              </Grid>
              <Grid item xs={6} sm={6}>
                <DatePickerWithLabel
                  label='Homeless End Date'
                  name='endDate'
                  data-testid="endDate"
                  required={false}
                  onChange={(value) => { valueChanged('endDate', value) }}
                  value={props.editData.endDate ?? null}
                />
              </Grid>
            </Grid>
            {residentOptions !== null && <SelectWithLabel
              name="homelessResidenceID"
              label="Primary Nighttime Residence"
              value={props.editData.homelessResidenceID ?? undefined}
              options={residentOptions}
              onChange={props.onChange}
              width='100%'
              required={true}
            />
            }
            <SelectWithLabel
              options={unaccompaniedOptions}
              label='Is Unaccompanied Youth'
              name='isUnaccompaniedYouth'
              onChange={props.onChange}
              value={props.editData.isUnaccompaniedYouth ?? undefined}
              width='100%'
              required={true}
            />
          </>
        )}

        {othersAffected.length > 0 &&
          <TextWithLabel name='othersAffected' label='Changing this Address will affect the following individuals:' value={othersAffected.join(', ')} />
        }
        {isdChanged && props.childID !== null &&
          <Box sx={{ p: '10px 0 3px 0' }}>
            <Alert severity="warning" sx={{ border: 1, borderColor: 'orange' }}>
              {districtISD.isOnboardedISD
                ? `It looks like this new primary address will move this child to be part of ${districtISD.isdName}.  Saving this change will transfer this student to ${districtISD.isdName} and assign a caseload to ${districtISD.isdIntakeProviderName ?? ' their intake provider'}.  All open enrollments will not be directly affected and will need to be dealt with manually.  Please confirm below before saving.`
                : 'The ISD this child is moving to does not have MiECC Services.  You will retain access to this child but there is no one in MiECC to help them in their new ISD. '}
            </Alert>
            {districtISD.isOnboardedISD && <CheckBoxWithLabel
              edge='start'
              label='Confirm Transfer'
              dataTestId="confirmTransfer"
              name='confirmTransfer'
              setValue={transferConfirmed}
              value={confirmTransfer}
            />
            }
          </Box>
        }
      </>
    )}
  </>)
}
